import { FieldInstance } from "@/types"
import { isBuiltInCaseColumn } from "@/types/caseColumns"
import { LabeledValue } from "@/types/labeledValue"
import { sortBy } from "lodash"

/**
 * Sorts a field and value dictionary based on field.order and field.section.
 * @param fieldAndValues - The field and values dictionary to be sorted.
 * @param fields - The fields to sort by.
 * @param sections - The section order to sort by. Takes and an array in the order to sort by.
 * @return Returns a sorted field and value dictionary.
 */
const sortFieldValuesDictionary = (fieldAndValues: { [key: string]: LabeledValue }, fields: FieldInstance[], sections: string[]) => {
	if (!fieldAndValues || Object.keys(fieldAndValues).length < 1) { throw new Error("Arguement 'fieldAndValues' cannot be undefined, null or empty.") }
	if (!fields || fields.length < 1) { throw new Error("Arguement 'fields' cannot be undefined, null or empty.") }
	if (!sections || sections.length < 1) { throw new Error("Arguement 'sections' cannot be undefined, null or empty.") }
	if (fields.some(f => f.order === null || f.order === undefined)) { throw new Error("Fields cannot contain an element with no order.") }
	if (fields.some(f => f.section === null || f.section === undefined)) { throw new Error("Fields cannot contain an element with no section.") }

	// Defines a function that will sort each field and value by the index of the section
	const sortBySection = (defaultValue: LabeledValue) => {
		if (isBuiltInCaseColumn(defaultValue.label)) {
			return 0 // ignore built-in case columns
		}

		var field = fields.find(f => f.field.id === defaultValue.label)
		if (field === undefined) { throw new Error("Could not find instance of field.") }
		var sectionIndex = sections.indexOf(field.section)
		return sectionIndex
	}

	// Define a function that will sort each field and value by it's order.
	const sortBySectionOrder = (defaultValue: LabeledValue) => {
		if (isBuiltInCaseColumn(defaultValue.label)) {
			return 0 // ignore built-in case columns
		}

		var field = fields.find(f => f.field.id === defaultValue.label)
		if (field === undefined) { throw new Error("Could not find instance of field.") }
		return field?.order
	}

	// Sort first by section, then by field order, then reduce it to an object of {Key:LabeledValue}.
	const result: { [key: string]: LabeledValue } = sortBy(fieldAndValues, [sortBySection, sortBySectionOrder])
		.reduce((result: {[key: string]: LabeledValue}, defaultValue) => {
			result[defaultValue.label] = defaultValue
			return result
		}, {})

	return result
}

export default sortFieldValuesDictionary
